// La liste des leads, en deux présentations : des cartes (par défaut) pour
// lire le signal, une table dense pour balayer vite. Même données, mêmes actions.
import { Archive, ArchiveRestore, Star } from "lucide-react";
import { RelativeTime } from "@/components/relative-time";
import { normalizeNamedContacts, type Lead, type LeadStatus } from "@shared/lib/leads-shared";
import { useT } from "@shared/client/locale";
import { hostOf } from "@shared/lib/offers-shared";
import { StatusDot } from "@shared/components/blocks";
import { Button } from "@shared/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { cn } from "@shared/lib/utils";
import { ContactAvatar } from "@shared/components/contact";
import { OrgLogo } from "@shared/components/org-logo";

export type LeadsLayout = "cards" | "table";

type ViewProps = {
  leads: Lead[];
  selectedId: string | null;
  readOnly: boolean;
  onOpen: (lead: Lead) => void;
  onPatch: (id: string, body: { favorite?: boolean; archived?: boolean }) => void;
};

type ActionProps = Pick<ViewProps, "readOnly" | "onPatch"> & { lead: Lead; className?: string };

/** Favori : l'étoile reste visible quand elle est allumée. */
function FavoriteButton({ lead, readOnly, onPatch, className }: ActionProps) {
  const t = useT();
  if (readOnly) return null;
  return (
    <Button
      aria-label={lead.favorite ? t.leads.views.removeFavoriteNamed(lead.name) : t.leads.views.addFavoriteNamed(lead.name)}
      aria-pressed={lead.favorite}
      className={cn("text-border-strong hover:text-muted-foreground", lead.favorite && "text-chart-4 hover:text-chart-4", className)}
      onClick={(event) => {
        event.stopPropagation();
        onPatch(lead.id, { favorite: !lead.favorite });
      }}
      size="icon-sm"
      title={lead.favorite ? t.leads.actions.removeFavorite : t.leads.actions.addFavorite}
      type="button"
      variant="ghost"
    >
      <Star className={cn(lead.favorite && "fill-current")} />
    </Button>
  );
}

/** Archivage : n'apparaît qu'au survol, sauf sur écran tactile. */
function ArchiveButton({ lead, readOnly, onPatch, className }: ActionProps) {
  const t = useT();
  if (readOnly) return null;
  return (
    <Button
      aria-label={lead.archived_at ? t.leads.views.restoreNamed(lead.name) : t.leads.views.archiveNamed(lead.name)}
      className={cn(
        "text-muted-foreground opacity-0 group-hover/lead:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 max-md:opacity-100",
        className,
      )}
      onClick={(event) => {
        event.stopPropagation();
        onPatch(lead.id, { archived: !lead.archived_at });
      }}
      size="icon-sm"
      title={lead.archived_at ? t.leads.actions.restore : t.leads.actions.archive}
      type="button"
      variant="ghost"
    >
      {lead.archived_at ? <ArchiveRestore /> : <Archive />}
    </Button>
  );
}

const STATUS_TONES: Record<LeadStatus, string> = {
  new: "info",
  to_contact: "warning",
  contacted: "neutral",
  replied: "active",
  won: "active",
  dismissed: "neutral",
};

/** Readiness tiers, compared on the stored (French) value; the label comes from the dictionary. */
function readinessDot(readiness: string | null | undefined) {
  if (readiness === "Demande active") return "bg-destructive";
  if (readiness === "Besoin explicite") return "bg-warning";
  return "bg-subtle-foreground";
}

const scoreOf = (lead: Lead) => lead.latest_signal?.priority ?? lead.osint_score;
const freshnessOf = (lead: Lead) => lead.latest_signal?.created_at || lead.last_seen_at;

function people(lead: Lead) {
  const named = normalizeNamedContacts(lead.contacts);
  if (named.length) return named.map((c) => ({ name: c.contact_name, role: c.contact_role, photo: c.contact_photo_url }));
  return lead.contact_name ? [{ name: lead.contact_name, role: lead.contact_role, photo: null }] : [];
}

/** Score sur 100 en anneau : une jauge bornée, lue au chiffre. */
export function ScoreRing({ score, size = 40 }: { score: number | null; size?: number }) {
  const t = useT();
  if (typeof score !== "number") return <span className="text-[13px] text-muted-foreground">—</span>;
  const r = size / 2 - 3;
  const c = 2 * Math.PI * r;
  return (
    <span aria-label={t.leads.views.scoreAria(score)} className="relative inline-grid flex-none place-items-center" style={{ width: size, height: size }}>
      <svg className="-rotate-90" height={size} width={size}>
        <circle className="stroke-muted" cx={size / 2} cy={size / 2} fill="none" r={r} strokeWidth={3} />
        <circle
          className="stroke-foreground"
          cx={size / 2}
          cy={size / 2}
          fill="none"
          r={r}
          strokeDasharray={c}
          strokeDashoffset={c * (1 - Math.max(0, Math.min(100, score)) / 100)}
          strokeLinecap="round"
          strokeWidth={3}
        />
      </svg>
      <span className="absolute text-[12.5px] font-semibold tabular-nums" style={{ fontSize: size * 0.32 }}>{score}</span>
    </span>
  );
}

function Avatars({ lead, max = 3 }: { lead: Lead; max?: number }) {
  const list = people(lead);
  if (!list.length) return null;
  return (
    <span className="flex -space-x-1">
      {list.slice(0, max).map((p) => (
        <ContactAvatar className="size-7 ring-2 ring-background" key={p.name} name={p.name} photo={p.photo} textClassName="text-[11px]" />
      ))}
      {list.length > max && (
        <i className="grid size-7 place-items-center rounded-full bg-secondary text-[11px] text-muted-foreground not-italic ring-2 ring-background">+{list.length - max}</i>
      )}
    </span>
  );
}

function Identity({ lead, logo = 36 }: { lead: Lead; logo?: number }) {
  const t = useT();
  const unread = !lead.opened_at && !lead.archived_at;
  return (
    <span className="flex min-w-0 flex-1 items-center gap-3">
      <OrgLogo name={lead.name} size={logo} website={lead.website} />
      <span className="grid min-w-0">
        <span className="flex min-w-0 items-center gap-2">
          <strong className={cn("truncate text-[14px]", unread ? "font-semibold" : "font-medium")}>{lead.name}</strong>
          {unread && (
            <span className="inline-flex flex-none items-center gap-1.5 text-[12px] font-medium text-primary">
              {/* Un point de 8 px qui pulse : il attire l'œil sans crier. Immobile si
                  le système demande moins d'animations. */}
              <span aria-hidden="true" className="relative inline-flex size-2">
                <span className="absolute inset-0 rounded-full bg-primary opacity-60 motion-safe:animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
                <span className="relative size-2 rounded-full bg-primary" />
              </span>
              {lead.reactivated_at ? t.leads.views.reactivated : t.leads.views.unread}
            </span>
          )}
        </span>
        <span className="truncate text-[12.5px] text-muted-foreground">
          {lead.website ? hostOf(lead.website) : lead.category}
        </span>
      </span>
    </span>
  );
}

function rowKeys(lead: Lead, onOpen: (lead: Lead) => void) {
  return {
    onClick: () => onOpen(lead),
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen(lead);
      }
    },
    role: "button",
    tabIndex: 0,
  } as const;
}

/** Table : une ligne par lead, pour balayer vite. */

export function LeadsRichTable({ leads, selectedId, readOnly, onOpen, onPatch }: ViewProps) {
  const t = useT();
  const columns = t.leads.views.columns;
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="w-10 pl-3 max-sm:pl-1"><span className="sr-only">{columns.favorite}</span></TableHead>
          <TableHead className="pl-1">{columns.organization}</TableHead>
          <TableHead className="max-md:hidden">{columns.signal}</TableHead>
          <TableHead className="max-lg:hidden">{columns.contact}</TableHead>
          <TableHead className="w-16 text-center">{columns.score}</TableHead>
          <TableHead className="w-28 max-sm:hidden">{columns.status}</TableHead>
          <TableHead className="w-12 pr-3"><span className="sr-only">{columns.actions}</span></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {leads.map((lead) => {
          const signal = lead.latest_signal;
          const first = people(lead)[0];
          return (
            <TableRow
              className={cn(
                "group/lead cursor-pointer",
                selectedId === lead.id && "bg-muted",
                lead.opened_at && "[&_p]:text-muted-foreground",
              )} key={lead.id} {...rowKeys(lead, onOpen)}>
              <TableCell className="pl-3 max-sm:pl-1"><FavoriteButton lead={lead} onPatch={onPatch} readOnly={readOnly} /></TableCell>
              <TableCell className="max-w-[280px] py-3 pl-1"><Identity lead={lead} /></TableCell>
              <TableCell className="max-w-[360px] whitespace-normal max-md:hidden">
                <p className="m-0 line-clamp-2 text-[13px] leading-snug">{signal?.market_clue || lead.category}</p>
                <span className="mt-1 flex items-center gap-2 text-[12px] text-muted-foreground">
                  {signal?.readiness && (
                    <span className="inline-flex items-center gap-1.5">
                      <i className={cn("size-1.5 rounded-full", readinessDot(signal.readiness))} />
                      {t.leads.readiness[signal.readiness] ?? signal.readiness}
                    </span>
                  )}
                  <RelativeTime value={freshnessOf(lead)} />
                </span>
              </TableCell>
              <TableCell className="max-lg:hidden">
                {first ? (
                  <span className="flex items-center gap-2.5">
                    <ContactAvatar className="size-7" name={first.name} photo={first.photo} textClassName="text-[11px]" />
                    <span className="grid min-w-0">
                      <span className="truncate text-[13px]">{first.name}</span>
                      <span className="max-w-[180px] truncate text-[12px] text-muted-foreground">{first.role}</span>
                    </span>
                  </span>
                ) : (
                  <span className="text-[12.5px] text-muted-foreground">{t.leads.views.contactToFind}</span>
                )}
              </TableCell>
              <TableCell className="text-center"><ScoreRing score={scoreOf(lead)} size={36} /></TableCell>
              <TableCell className="max-sm:hidden"><StatusDot tone={STATUS_TONES[lead.status]}>{t.leads.statuses[lead.status]}</StatusDot></TableCell>
              <TableCell className="pr-3"><ArchiveButton lead={lead} onPatch={onPatch} readOnly={readOnly} /></TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** Cartes : le signal en avant, pour lire. */

export function LeadsCards({ leads, selectedId, readOnly, onOpen, onPatch }: ViewProps) {
  const t = useT();
  return (
    <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
      {leads.map((lead) => {
        const signal = lead.latest_signal;
        const first = people(lead)[0];
        return (
          <article
            className={cn(
              "group/lead flex cursor-pointer flex-col gap-4 rounded-lg border p-4 transition-colors hover:border-border-strong hover:bg-muted/40",
              // Un lead jamais ouvert se détache : cadre plus net ; les autres s'effacent un peu.
              !lead.opened_at && !lead.archived_at ? "border-foreground/30" : "[&_p]:text-muted-foreground",
              selectedId === lead.id && "border-border-strong bg-muted/40",
            )}
            key={lead.id}
            {...rowKeys(lead, onOpen)}
          >
            <header className="flex items-start justify-between gap-3">
              <Identity lead={lead} logo={40} />
              <ScoreRing score={scoreOf(lead)} />
            </header>
            <div className="grid gap-2">
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
                {signal?.readiness && (
                  <span className="inline-flex items-center gap-1.5">
                    <i className={cn("size-1.5 rounded-full", readinessDot(signal.readiness))} />
                    {t.leads.readiness[signal.readiness] ?? signal.readiness}
                  </span>
                )}
                {lead.status !== "new" && <StatusDot tone={STATUS_TONES[lead.status]}>{t.leads.statuses[lead.status]}</StatusDot>}
              </span>
              <p className="m-0 line-clamp-3 text-[14px] leading-[1.5]">{signal?.market_clue || lead.category}</p>
            </div>
            <footer className="mt-auto flex items-center justify-between gap-3 border-t pt-3 text-[12.5px] text-muted-foreground">
              <span className="flex min-w-0 items-center gap-2">
                <Avatars lead={lead} />
                {first && <span className="truncate">{first.name}</span>}
              </span>
              <span className="-my-1 -mr-1.5 flex flex-none items-center gap-0.5">
                <RelativeTime className="mr-1" value={freshnessOf(lead)} />
                <ArchiveButton lead={lead} onPatch={onPatch} readOnly={readOnly} />
                <FavoriteButton lead={lead} onPatch={onPatch} readOnly={readOnly} />
              </span>
            </footer>
          </article>
        );
      })}
    </div>
  );
}
